// VALD test catalogue — labels, units and coach-facing explanations, ported from
// the old app. Field keys match what the VALD sync writes into
// testing_results.values (see netlify/functions/lib/vald.js METRIC_MAP).

export interface TestField {
  key: string
  label: string
  /** Overrides the test's default unit when present. */
  unit?: string
}

export interface TestMetric {
  id: string
  label: string
  /** Default unit for fields that don't specify their own. */
  unit: string
  /** Coach-facing detail. */
  description: string
  /** Plain-English version for parents. */
  plain: string
  fields: TestField[]
}

export const TESTING_METRICS: TestMetric[] = [
  {
    id: 'cmj',
    label: 'Countermovement Jump',
    unit: 'cm',
    description:
      'Measures explosive lower body power and neuromuscular readiness. Jump height tracks overall athleticism and training progress. L/R asymmetry flags muscle imbalances that increase injury risk — anything above 15% warrants attention.',
    plain:
      "A two-footed jump on a force plate. It shows how much power your athlete can produce through their legs, and whether both legs are contributing evenly. It is the best quick check of athleticism and how fresh they are.",
    fields: [
      { key: 'height', label: 'Jump Height' },
      { key: 'asymmetry', label: 'L/R Asymmetry', unit: '%' },
      { key: 'peak_force', label: 'Peak Force', unit: 'N' },
      { key: 'rel_power', label: 'Relative Power', unit: 'W/kg' },
    ],
  },
  {
    id: 'sl_cmj',
    label: 'Single Leg CMJ',
    unit: 'cm',
    description:
      'Single leg jumping isolates each limb independently, making it the most sensitive test for detecting strength and power imbalances. Critical for return-to-sport decisions and monitoring athletes recovering from lower limb injuries.',
    plain:
      "The same jump, but on one leg at a time. Comparing left to right is the clearest way to spot one leg being weaker than the other, which matters most when coming back from an injury.",
    fields: [
      { key: 'left', label: 'Left Height' },
      { key: 'right', label: 'Right Height' },
      { key: 'asymmetry', label: 'Asymmetry', unit: '%' },
    ],
  },
  {
    id: 'imtp',
    label: 'Max Iso Mid Thigh Pull',
    unit: 'N',
    description:
      'Measures maximum lower body strength and force production without movement. A strong IMTP correlates directly with athletic performance — sprinting, jumping, and change of direction. Also used as a readiness marker; a drop in force output can indicate fatigue or overtraining.',
    plain:
      "A maximum pull against an immovable bar. It measures raw leg and hip strength. Bigger numbers here generally mean faster sprinting and better change of direction.",
    fields: [
      { key: 'peak_force', label: 'Peak Force' },
      { key: 'relative_force', label: 'Relative Force', unit: 'N/kg' },
      { key: 'asymmetry', label: 'L/R Asymmetry', unit: '%' },
    ],
  },
  {
    id: 'hip_abd',
    label: 'Hip Abduction',
    unit: 'N',
    description:
      'Measures the strength of the glutes and hip stabilisers on each side. Weak hip abductors are one of the primary contributors to knee valgus, IT band issues, and ACL injury risk. Essential for basketball players who need lateral stability.',
    plain:
      "A squeeze test measuring the muscles that push the knees apart. These muscles keep the knee tracking properly when running and cutting, so weakness here is linked to knee and hip problems.",
    fields: [
      { key: 'left', label: 'Left Max Force' },
      { key: 'right', label: 'Right Max Force' },
      { key: 'asymmetry', label: 'Asymmetry', unit: '%' },
    ],
  },
  {
    id: 'hip_add',
    label: 'Hip Adduction',
    unit: 'N',
    description:
      'Measures inner thigh and groin strength. The abduction-to-adduction ratio is a key indicator of groin strain risk — athletes with significantly stronger adductors than abductors are at elevated risk. Tracking both together gives a complete picture of hip stability.',
    plain:
      "A squeeze test measuring the inner thigh and groin muscles. Comparing these against the outer hip muscles is one of the best early warnings for groin strains.",
    fields: [
      { key: 'left', label: 'Left Max Force' },
      { key: 'right', label: 'Right Max Force' },
      { key: 'asymmetry', label: 'Asymmetry', unit: '%' },
    ],
  },
  {
    id: 'rsi',
    label: 'Reactive Strength Index',
    unit: '',
    description:
      'RSI measures the ability to rapidly absorb force and rebound — the foundation of all explosive athletic movement. Calculated as jump height divided by ground contact time. High RSI = more elastic, reactive athlete. Tracks plyometric adaptation and is a sensitive daily readiness indicator.',
    plain:
      "Measures how quickly your athlete can hit the ground and bounce straight back off it. It is the springiness behind sprinting and jumping, and it drops when they are tired.",
    fields: [
      { key: 'rsi', label: 'RSI Score' },
      { key: 'contact_time', label: 'Contact Time', unit: 'ms' },
      { key: 'flight_time', label: 'Flight Time', unit: 'ms' },
    ],
  },
{
    id: 'sl_hop',
    label: 'Single Leg Hop Test',
    unit: '',
    description:
      'Reactive strength measured one leg at a time. Because it isolates each limb under fast, repeated loading, it is one of the most sensitive return-to-play markers — a limb that is strong but slow to react will show up here when a two-footed test looks fine.',
    plain:
      "Repeated small hops on one leg at a time, measuring how quickly they bounce off the ground. Comparing left with right shows whether one leg is still lagging behind the other.",
    fields: [
      { key: 'left', label: 'Left RSI' },
      { key: 'right', label: 'Right RSI' },
      { key: 'asymmetry', label: 'Asymmetry', unit: '%' },
    ],
  },
  {
    id: 'sj',
    label: 'Squat Jump',
    unit: 'cm',
    description:
      'A jump from a held squat position with no countermovement. Removing the stretch-shortening cycle isolates pure concentric power — comparing it against the countermovement jump shows how much an athlete gains from elastic energy.',
    plain:
      "A jump from a still squat position, with no dip beforehand. It measures raw pushing power without the natural bounce, which tells us how much of their jump comes from strength versus springiness.",
    fields: [
      { key: 'height', label: 'Jump Height' },
      { key: 'peak_force', label: 'Peak Force', unit: 'N' },
      { key: 'rel_power', label: 'Relative Power', unit: 'W/kg' },
    ],
  },
  {
    id: 'dj',
    label: 'Drop Jump',
    unit: 'cm',
    description:
      'Dropping from a box and rebounding as fast as possible. It stresses the stretch-shortening cycle harder than a countermovement jump and, with contact time, gives the clearest read on elastic qualities and landing mechanics.',
    plain:
      "Stepping off a box and immediately jumping as high and as fast as possible on landing. It shows how well they absorb force and turn it straight back into a jump.",
    fields: [
      { key: 'height', label: 'Jump Height' },
      { key: 'contact_time', label: 'Contact Time', unit: 'ms' },
      { key: 'rsi', label: 'RSI' },
    ],
  },
]

/** Asymmetry at or above this is worth flagging to the coach. */
export const ASYMMETRY_WARN = 15

/**
 * Metrics where a SMALLER number is the better outcome — asymmetry (limbs more
 * even) and ground contact time (more reactive). Trend colouring must invert
 * for these, or an improvement reads as a decline.
 */
export const LOWER_IS_BETTER = new Set(['asymmetry', 'contact_time'])
